/**
 * Slice 19d — exemplar-management subsection rendered inside the template editor
 * when editing an existing template. Lets the trainer:
 *   - see the currently-linked exemplar post ids as chips (with remove)
 *   - open an indexed-ads picker to link more (capped, server-validated on Save)
 *   - rerun AI drafting on the linked exemplars and apply the draft fields
 *
 * Slice 19e — the "Rerun AI drafting" call also forwards the trainer's custom
 * exemplar texts (rendered in a sibling field; see
 * `AdsLibraryTemplateCustomExemplarsField`) so the model sees both sources.
 */
import { useCallback, useState } from "react";
import {
  assistAdsLibraryDraft,
  type AdsLibraryAssistDraft,
} from "@/lib/adsAssistantClient";
import { AdsLibraryIndexedPostPicker } from "@/components/ads-assistant/AdsLibraryIndexedPostPicker";

const EXEMPLAR_CAP = 5;

type Props = {
  scopedOrgId: string | null;
  selectedIds: string[];
  setSelectedIds: (ids: string[]) => void;
  /** Slice 19e — forwarded to the rerun assist call so the model sees both sources. */
  customExemplarTexts: string[];
  setError: (e: string | null) => void;
  setBanner: (b: string | null) => void;
  /**
   * Applies fields from the rerun AI draft to the editor without clearing the
   * already-linked exemplars (slice 19d preservation rule).
   */
  applyRerunDraft: (d: AdsLibraryAssistDraft) => void;
};

export function AdsLibraryTemplateExemplarsSection(props: Props) {
  const {
    scopedOrgId,
    selectedIds,
    setSelectedIds,
    customExemplarTexts,
    setError,
    setBanner,
    applyRerunDraft,
  } = props;
  const [pickerOpen, setPickerOpen] = useState(false);
  const [rerunBusy, setRerunBusy] = useState(false);

  const toggle = useCallback(
    (id: string) => {
      const has = selectedIds.includes(id);
      if (has) {
        setSelectedIds(selectedIds.filter((x) => x !== id));
        return;
      }
      if (selectedIds.length >= EXEMPLAR_CAP) return;
      setSelectedIds([...selectedIds, id]);
    },
    [selectedIds, setSelectedIds],
  );

  const removeOne = useCallback(
    (id: string) => setSelectedIds(selectedIds.filter((x) => x !== id)),
    [selectedIds, setSelectedIds],
  );

  const runRerun = useCallback(async () => {
    if (selectedIds.length < 1 && customExemplarTexts.length < 1) return;
    setRerunBusy(true);
    setError(null);
    const r = await assistAdsLibraryDraft({
      intent:
        "Refine the existing template draft using the currently linked exemplar set (indexed posts + trainer-written custom exemplars) as ground truth (re-derive copy shell, archetype tags, and category).",
      pastedAd: "",
      includeSampleAdCopy: false,
      exampleSocialPostIds: selectedIds,
      customExemplarTexts,
      target: "template",
    });
    setRerunBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    if (!r.data.draft?.template) {
      setError("AI did not return a template draft. Try a different set or platform.");
      return;
    }
    applyRerunDraft(r.data.draft);
    setBanner("AI rerun applied — review and Save changes.");
  }, [selectedIds, customExemplarTexts, applyRerunDraft, setError, setBanner]);

  return (
    <section className="space-y-2 rounded-lg border border-amber-200/80 bg-amber-50/40 p-2 dark:border-amber-900/50 dark:bg-amber-950/15">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h5 className="text-[11px] font-semibold uppercase tracking-wide text-amber-900 dark:text-amber-200">
          Linked exemplars ({selectedIds.length}/{EXEMPLAR_CAP})
        </h5>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setPickerOpen((v) => !v)}
            className="rounded border border-amber-300 bg-white px-2 py-0.5 text-[11px] font-medium text-amber-900 hover:bg-amber-50 dark:border-amber-800 dark:bg-zinc-950 dark:text-amber-200 dark:hover:bg-amber-950/40"
          >
            {pickerOpen ? "Close picker" : "Browse indexed ads"}
          </button>
          <button
            type="button"
            onClick={() => void runRerun()}
            disabled={rerunBusy || (selectedIds.length < 1 && customExemplarTexts.length < 1)}
            className="rounded border border-violet-700 bg-violet-700 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-violet-800 disabled:opacity-50"
          >
            {rerunBusy ? "Rerunning…" : "Rerun AI drafting"}
          </button>
        </div>
      </div>

      {selectedIds.length ? (
        <ul className="flex flex-wrap gap-1">
          {selectedIds.map((id) => (
            <li
              key={id}
              className="inline-flex items-center gap-1 rounded border border-amber-300 bg-white px-1.5 py-0.5 text-[10px] font-mono text-amber-900 dark:border-amber-800 dark:bg-zinc-950 dark:text-amber-200"
            >
              {id.slice(0, 8)}…
              <button
                type="button"
                onClick={() => removeOne(id)}
                aria-label={`Unlink exemplar ${id.slice(0, 8)}`}
                className="text-amber-700 hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-100"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[11px] text-amber-900/85 dark:text-amber-200/85">
          No exemplars linked yet. Use “Browse indexed ads” to attach saved posts as canonical examples (sets retention-safe
          guard, doesn’t change each post’s template badge).
        </p>
      )}

      {pickerOpen ? (
        <AdsLibraryIndexedPostPicker
          scopedOrgId={scopedOrgId}
          selectedIds={selectedIds}
          onToggle={toggle}
          maxSelected={EXEMPLAR_CAP}
        />
      ) : null}
    </section>
  );
}
